import * as React from 'react';
import SortableTableHead, { HeadCell, Order } from '../Utilities/SortableTableHead';
import InstructingPartyViewModel from './ViewModels/InstructingPartyListViewmodel';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import authService from '../api-authorization/AuthorizeService';
import { ResponseType, TokenRefreshContext } from '../Contexts/TokenRefreshContext';
import InstructingPartyTableRow from './InstructingPartyTableRow';
import CreateInstructingParty from './InstructingParty/CreateInstructingParty';
import EditInstructingParty from './InstructingParty/EditInstructingParty';
import IconButton from '@mui/material/IconButton/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';


const headCells: HeadCell<InstructingPartyViewModel>[] = [
    { id: 'companyName', property: 'CompanyName', align: "left", disablePadding: false, label: 'Instructing Company' },
    { id: 'contactName', property: 'ContactName', align: "left", disablePadding: false, label: 'Contact Name' },
    { id: 'jobTitle', property: 'JobTitle', align: "left", disablePadding: false, label: 'Job Title' },
    { id: 'email', property: 'Email', align: "left", disablePadding: false, label: 'Email' },
    { id: 'office', property: 'Office', align: "left", disablePadding: false, label: 'Office' },
    { id: 'created', property: 'Created', align: "left", disablePadding: false, label: 'Created' }
];

export default function InstructingPartyTable() {
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof InstructingPartyViewModel>('companyName');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [results, setResults] = React.useState<InstructingPartyViewModel[]>([]);
    const [search, setSearch] = React.useState('');
    const [count, setCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [openAdd, setOpenAdd] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<number | null>(null);

    React.useEffect(() => {
        getData();
    }, [page, rowsPerPage, order, orderBy]);

    const toggleOpenAdd = () => setOpenAdd((prev) => !prev);

    const closeAdd = (refresh: boolean) => {
        if (refresh) getData();
        setOpenAdd(false);
    }

    const closeEdit = (refresh: boolean) => {
        if (refresh) getData();
        setSelectedRow(null);
    }

    const getData = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`InstructingParty/GetInstructingPartyTable?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&search=${search}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const clearSearch = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`InstructingParty/GetInstructingPartyTable?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&search=${''}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof InstructingPartyViewModel) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.target.value);
        if (page === 0 && event.target.value.length === 0) {
            setPage(0);
            clearSearch();
        }
    }

    const searchData = () => {
        if (page === 0) {
            getData();
        }
        setPage(0);
    }

    const keyPress = (event: any) => {
        if (event.keyCode === 13) {
            getData();
        }
    }
    const cancelSearch = () => {
        setSearch('');
        setPage(0);
        clearSearch();
    }
    const emptyRows = page > 0 ? Math.max(0, rowsPerPage - results.length) : 0;
    return (
        <Paper sx={{ padding: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Typography variant="h5">Instructing Parties</Typography>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    <TextField
                                        id="outlined-search"
                                        type="text"
                                        color="primary"
                                        size="small"
                                        onChange={handleSearch}
                                        value={search}
                                        placeholder="Search..."
                                        onKeyDown={keyPress}
                                        InputProps={{
                                            endAdornment: search && (
                                                <IconButton
                                                    size="small"
                                                    aria-label="clear search"
                                                    onClick={() => cancelSearch()}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button aria-label={"Search Instructing Parties"} onClick={searchData} variant="contained" >Search</Button>
                                </Grid>
                                <Grid item>
                                    <Button aria-label={"Create Instructing Party"} variant="contained" onClick={toggleOpenAdd} >Add Instructing Party</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer>
                        <ResponsiveTable
                            aria-labelledby="tableTitle"
                            size={'medium'}
                            aria-label="Instructing Party table"
                        >
                            <SortableTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                headCells={headCells}
                                lastCells={<TableCell colSpan={2} />}
                            />
                            <TableBody>
                                {(!loading && results) && results.map((row: InstructingPartyViewModel) => {
                                    return (<InstructingPartyTableRow key={row.id} row={row} editRow={(id: number) => setSelectedRow(id)} refresh={getData} />);
                                })
                                }
                                {count <= 0 && !loading &&
                                    <TableRow
                                        sx={{
                                            height: 53 * rowsPerPage,
                                        }}
                                    >
                                        <TableCell colSpan={headCells.length + 1} align="center">No Results Found</TableCell>
                                    </TableRow>
                                }
                                {count > 0 && emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 53 * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={headCells.length + 1} />
                                    </TableRow>
                                )}
                                {loading &&
                                    <TableRow
                                        sx={{
                                            height: 53 * rowsPerPage,
                                        }}
                                    >
                                        <TableCell colSpan={headCells.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </ResponsiveTable>
                    </TableContainer>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={count}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <CreateInstructingParty open={openAdd} onClose={closeAdd} />
            <EditInstructingParty open={selectedRow !== null} onClose={closeEdit} instructingPartyId={selectedRow} />
        </Paper>
    );
}